import { useContext, useState } from "react";
import { ButtonDS, GlobalIcon } from "get-life-storybook-ts";
import { I18nContext } from "../../../../../context/i18n.context";
import numberFormat from "../../../../../utils/Utils";

interface NeedsCalculatorProps {
  currentCapital: number;
  onClose: () => void;
  onRequestIncrease: () => void;
}

type Step = "intro" | "dependents" | "debt" | "income" | "result";

const STEPS: Step[] = ["intro", "dependents", "debt", "income", "result"];

const NeedsCalculator = ({
  currentCapital,
  onClose,
  onRequestIncrease,
}: NeedsCalculatorProps) => {
  const {
    state: { translate },
  } = useContext(I18nContext);

  const [step, setStep] = useState<Step>("intro");
  const [dependents, setDependents] = useState(0);
  const [debt, setDebt] = useState(150000);
  const [years, setYears] = useState(5);
  const [income, setIncome] = useState(30000);

  const currentIdx = STEPS.indexOf(step);
  const recommendation = debt + income * years + dependents * 50000;
  const isCovered = recommendation <= currentCapital;
  const progress = step === "intro" ? 0 : step === "result" ? 100 : ((currentIdx - 1) / 3) * 100;

  const goNext = () => {
    const next = STEPS[currentIdx + 1];
    if (next) setStep(next);
  };

  const goBack = () => {
    const prev = STEPS[currentIdx - 1];
    if (prev) setStep(prev);
  };

  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    zIndex: 1000,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: "#fff",
    borderRadius: 16,
    width: "100%",
    maxWidth: 560,
    maxHeight: "90vh",
    overflow: "auto",
    position: "relative",
  };

  const headerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px 24px 0",
  };

  const bodyStyle: React.CSSProperties = {
    padding: "24px 24px 32px",
  };

  const counterBtnStyle = (active?: boolean): React.CSSProperties => ({
    width: 48,
    height: 48,
    borderRadius: 12,
    border: "none",
    backgroundColor: active ? "#4f46e5" : "#f3f4f6",
    color: active ? "#fff" : "#374151",
    fontSize: 20,
    fontWeight: 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.15s",
  });

  const dependentCardStyle = (selected: boolean): React.CSSProperties => ({
    flex: 1,
    padding: "20px 12px",
    borderRadius: 12,
    border: `2px solid ${selected ? "#4f46e5" : "#e5e7eb"}`,
    backgroundColor: selected ? "#eef2ff" : "#fff",
    cursor: "pointer",
    textAlign: "center",
    transition: "all 0.15s",
    minWidth: 70,
  });

  const sliderTrackStyle: React.CSSProperties = {
    width: "100%",
    height: 8,
    appearance: "none" as any,
    WebkitAppearance: "none" as any,
    background: `linear-gradient(to right, #4f46e5 0%, #4f46e5 var(--val), #e5e7eb var(--val), #e5e7eb 100%)`,
    borderRadius: 4,
    outline: "none",
    cursor: "pointer",
  };

  // ── INTRO ─────────────────────────────────────────────────────────────
  const IntroStep = () => (
    <div style={{ textAlign: "center" }}>
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          backgroundColor: "#eef2ff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 20px",
        }}
      >
        <GlobalIcon iconName="CalculatorIcon" size="S" color="#4f46e5" />
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: "#111827", margin: "0 0 12px" }}>
        {translate("newDashboard.calculator.title")}
      </h2>
      <p style={{ fontSize: 15, color: "#6b7280", lineHeight: 1.6, margin: "0 0 28px" }}>
        {translate("newDashboard.calculator.intro").replace(
          "{capital}",
          numberFormat(currentCapital, "capital")
        )}
      </p>
      <ButtonDS
        label={translate("newDashboard.calculator.start")}
        buttonType="primary"
        size="40"
        onClick={goNext}
      />
    </div>
  );

  // ── STEP 1: DEPENDENTS ────────────────────────────────────────────────
  const DependentsStep = () => (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: "#111827", margin: "0 0 8px" }}>
        {translate("newDashboard.calculator.step1.title")}
      </h2>
      <p style={{ fontSize: 14, color: "#9ca3af", margin: "0 0 28px" }}>
        {translate("newDashboard.calculator.step1.subtitle")}
      </p>
      <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
        {[0, 1, 2, 3].map((n) => (
          <button
            key={n}
            onClick={() => setDependents(n)}
            style={dependentCardStyle(dependents === n)}
          >
            <p style={{ fontSize: 28, fontWeight: 800, color: dependents === n ? "#4f46e5" : "#374151", margin: 0 }}>
              {n === 3 ? "3+" : n}
            </p>
            <p style={{ fontSize: 12, color: dependents === n ? "#6366f1" : "#9ca3af", margin: "4px 0 0" }}>
              {n === 0 ? "Ninguna" : n === 1 ? "persona" : "personas"}
            </p>
          </button>
        ))}
      </div>
    </div>
  );

  // ── STEP 2: DEBT ──────────────────────────────────────────────────────
  const DebtStep = () => {
    const debtPercent = Math.min((debt / 500000) * 100, 100);
    return (
      <div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: "#111827", margin: "0 0 8px" }}>
          {translate("newDashboard.calculator.step2.title")}
        </h2>
        <p style={{ fontSize: 14, color: "#9ca3af", margin: "0 0 28px" }}>
          {translate("newDashboard.calculator.step2.subtitle")}
        </p>

        {/* Big number display */}
        <div
          style={{
            textAlign: "center",
            padding: "20px 0 28px",
          }}
        >
          <p style={{ fontSize: 36, fontWeight: 800, color: "#312e81", margin: 0 }}>
            {numberFormat(debt, "capital")}
          </p>
        </div>

        {/* Slider */}
        <div style={{ padding: "0 8px" }}>
          <input
            type="range"
            min={0}
            max={500000}
            step={5000}
            value={debt}
            onChange={(e) => setDebt(Number(e.target.value))}
            style={{ ...sliderTrackStyle, "--val": `${debtPercent}%` } as any}
          />
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
            <span style={{ fontSize: 12, color: "#9ca3af" }}>0 €</span>
            <span style={{ fontSize: 12, color: "#9ca3af" }}>500.000 €</span>
          </div>
        </div>
      </div>
    );
  };

  // ── STEP 3: INCOME ────────────────────────────────────────────────────
  const IncomeStep = () => {
    const yearsPercent = ((years - 1) / 9) * 100;
    return (
      <div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: "#111827", margin: "0 0 8px" }}>
          {translate("newDashboard.calculator.step3.title")}
        </h2>
        <p style={{ fontSize: 14, color: "#9ca3af", margin: "0 0 24px" }}>
          {translate("newDashboard.calculator.step3.subtitle")}
        </p>

        {/* Years slider */}
        <div style={{ marginBottom: 28 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 12 }}>
            {translate("newDashboard.calculator.step3.years")}
          </label>
          <div style={{ textAlign: "center", marginBottom: 16 }}>
            <span style={{ fontSize: 36, fontWeight: 800, color: "#312e81" }}>{years}</span>
            <span style={{ fontSize: 16, fontWeight: 500, color: "#6b7280", marginLeft: 4 }}>
              {years === 1 ? "año" : "años"}
            </span>
          </div>
          <div style={{ padding: "0 8px" }}>
            <input
              type="range"
              min={1}
              max={10}
              step={1}
              value={years}
              onChange={(e) => setYears(Number(e.target.value))}
              style={{ ...sliderTrackStyle, "--val": `${yearsPercent}%` } as any}
            />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
              <span style={{ fontSize: 12, color: "#9ca3af" }}>1 año</span>
              <span style={{ fontSize: 12, color: "#9ca3af" }}>10 años</span>
            </div>
          </div>
        </div>

        {/* Income input */}
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 8 }}>
            {translate("newDashboard.calculator.step3.income")}
          </label>
          <div style={{ position: "relative" }}>
            <input
              type="number"
              min={0}
              step={1000}
              value={income || ""}
              onChange={(e) => setIncome(Math.max(0, Number(e.target.value)))}
              style={{
                width: "100%",
                padding: "14px 40px 14px 16px",
                fontSize: 18,
                fontWeight: 600,
                border: "1px solid #d1d5db",
                borderRadius: 10,
                outline: "none",
                color: "#111827",
              }}
              placeholder="30.000"
            />
            <span
              style={{
                position: "absolute",
                right: 16,
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: 16,
                fontWeight: 600,
                color: "#9ca3af",
              }}
            >
              €
            </span>
          </div>
        </div>
      </div>
    );
  };

  // ── RESULT ────────────────────────────────────────────────────────────
  const ResultStep = () => (
    <div style={{ textAlign: "center" }}>
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          backgroundColor: isCovered ? "#ecfdf5" : "#fffbeb",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 20px",
        }}
      >
        <GlobalIcon
          iconName={isCovered ? "CircleCheckFilledIcon" : "AlertCircleIcon"}
          size="S"
          color={isCovered ? "#059669" : "#d97706"}
        />
      </div>

      <h2
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: isCovered ? "#065f46" : "#92400e",
          margin: "0 0 12px",
        }}
      >
        {isCovered
          ? translate("newDashboard.calculator.result.covered.title")
          : translate("newDashboard.calculator.result.under.title").replace(
              "{recommended}",
              numberFormat(recommendation, "capital")
            )}
      </h2>

      <p style={{ fontSize: 15, color: "#6b7280", lineHeight: 1.6, margin: "0 0 24px" }}>
        {isCovered
          ? translate("newDashboard.calculator.result.covered.desc").replace(
              "{capital}",
              numberFormat(currentCapital, "capital")
            )
          : translate("newDashboard.calculator.result.under.desc").replace(
              "{capital}",
              numberFormat(currentCapital, "capital")
            )}
      </p>

      {/* Breakdown */}
      <div
        style={{
          backgroundColor: "#f9fafb",
          borderRadius: 10,
          padding: 16,
          margin: "0 0 24px",
          textAlign: "left",
        }}
      >
        <p style={{ fontSize: 13, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, margin: "0 0 12px" }}>
          Desglose de tu cálculo
        </p>
        {[
          { label: `${dependents} dependiente${dependents !== 1 ? "s" : ""} × 50.000 €`, value: dependents * 50000 },
          { label: "Deudas e hipoteca", value: debt },
          { label: `Ingresos (${numberFormat(income, "capital")} × ${years} año${years !== 1 ? "s" : ""})`, value: income * years },
        ].map((row, idx) => (
          <div
            key={idx}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 0",
              borderBottom: idx < 2 ? "1px solid #e5e7eb" : "none",
            }}
          >
            <span style={{ fontSize: 14, color: "#374151" }}>{row.label}</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>
              {numberFormat(row.value, "capital")}
            </span>
          </div>
        ))}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: 12,
            marginTop: 4,
            borderTop: "2px solid #d1d5db",
          }}
        >
          <span style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>Total recomendado</span>
          <span style={{ fontSize: 15, fontWeight: 800, color: isCovered ? "#059669" : "#d97706" }}>
            {numberFormat(recommendation, "capital")}
          </span>
        </div>
      </div>

      {/* Visual comparison bar */}
      {!isCovered && (
        <div style={{ margin: "0 0 28px", padding: "0 16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#d97706" }}>
              Actual: {numberFormat(currentCapital, "capital")}
            </span>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#059669" }}>
              Recomendado: {numberFormat(recommendation, "capital")}
            </span>
          </div>
          <div style={{ height: 12, backgroundColor: "#f3f4f6", borderRadius: 6, overflow: "hidden", position: "relative" }}>
            <div
              style={{
                height: "100%",
                width: `${Math.min((currentCapital / recommendation) * 100, 100)}%`,
                backgroundColor: "#d97706",
                borderRadius: 6,
                transition: "width 0.5s ease",
              }}
            />
          </div>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {!isCovered && (
          <ButtonDS
            label={translate("newDashboard.calculator.result.cta")}
            buttonType="primary"
            size="40"
            onClick={onRequestIncrease}
            disabled
          />
        )}
        <ButtonDS
          label={translate("newDashboard.calculator.result.done")}
          buttonType={isCovered ? "primary" : "secondary"}
          size="40"
          onClick={onClose}
        />
      </div>
    </div>
  );

  const stepContent: Record<Step, JSX.Element> = {
    intro: <IntroStep />,
    dependents: <DependentsStep />,
    debt: <DebtStep />,
    income: <IncomeStep />,
    result: <ResultStep />,
  };

  const showNavButtons = step !== "intro" && step !== "result";

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header with close + progress */}
        <div style={headerStyle}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#9ca3af" }}>
            {step !== "intro" && step !== "result"
              ? `${currentIdx} / 3`
              : ""}
          </span>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "none",
              cursor: "pointer",
              padding: 6,
              borderRadius: 6,
              display: "flex",
            }}
          >
            <GlobalIcon iconName="XIcon" size="XS" color="#9ca3af" />
          </button>
        </div>

        {/* Progress bar */}
        {step !== "intro" && (
          <div style={{ padding: "0 24px", marginTop: 8 }}>
            <div style={{ height: 4, backgroundColor: "#f3f4f6", borderRadius: 2, overflow: "hidden" }}>
              <div
                style={{
                  height: "100%",
                  width: `${progress}%`,
                  backgroundColor: "#4f46e5",
                  borderRadius: 2,
                  transition: "width 0.3s ease",
                }}
              />
            </div>
          </div>
        )}

        {/* Body */}
        <div style={bodyStyle}>
          {stepContent[step]}
        </div>

        {/* Navigation buttons */}
        {showNavButtons && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "0 24px 24px",
            }}
          >
            <ButtonDS
              label={translate("newDashboard.calculator.back")}
              buttonType="secondary"
              size="40"
              onClick={goBack}
            />
            <ButtonDS
              label={translate("newDashboard.calculator.next")}
              buttonType="primary"
              size="40"
              onClick={goNext}
            />
          </div>
        )}
      </div>

      {/* Slider CSS — needed for custom range styling */}
      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #4f46e5;
          border: 3px solid #fff;
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
          cursor: pointer;
          margin-top: -8px;
        }
        input[type="range"]::-moz-range-thumb {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #4f46e5;
          border: 3px solid #fff;
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
          cursor: pointer;
        }
        input[type="range"]::-webkit-slider-runnable-track {
          height: 8px;
          border-radius: 4px;
        }
      `}</style>
    </div>
  );
};

export default NeedsCalculator;
